/*!
 * law-ui.js — the page of /signage-law-check.
 *
 * Owns I/O and copy; every verdict comes from law-core.js (SignageLaw), every
 * page reading from law-extract.js (SignageLawExtract). Data: our lexicon and
 * English glossary (public/data/signage-law/), the anglicism dictionary for
 * words the lexicon does not know (public/data/anglicisms/ru.json, loaded after
 * the first paint). Budgets are the /anglicism-checker ones (config/anglicism.php):
 * text — a soft daily counter of characters in this browser (shared key
 * ac_text_chars); pages of other sites (a page by link, every page of a whole
 * site) — the SERVER's count shared by both tools (ForeignPageQuota: 5 a day
 * without Премиум, 500 with it, up to 200 in one site check, one check at a
 * time), drawn by the site's shared limit block (__toolLimit.serverCount).
 */
(function () {
    'use strict';
    const C = window.__SLC || {};
    const T = C.T || {};
    const L = window.SignageLaw;
    const X = window.SignageLawExtract;
    if (!L || !X) return;
    const SLUG = C.slug || 'signage-law-check';
    const $ = (id) => document.getElementById(id);
    const settings = window.__toolSettings || { save: () => false, load: (_a, _b, d) => d };
    const ae = (ev, d) => { try { if (window.__ae) window.__ae(ev, Object.assign({ slug: SLUG }, d || {})); } catch (_) {} };
    const toast = (m, type) => { try { if (window.__toast) window.__toast(m, type || 'info'); } catch (_) {} };
    const fail = (err, label, say) => {
        try { if (window.__handleError) { window.__handleError(err, label, { say }); return; } } catch (_) {}
        toast(say || String(err && err.message || err), 'error');
    };
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const tr = (s, map) => String(s || '').replace(/:([a-z_]+)/g, (m, k) => (map && map[k] != null ? map[k] : m));
    const V = T.v || {};
    const intlLoc = () => { try { return window.__intlLocale ? window.__intlLocale() : (C.locale || 'ru'); } catch (_) { return 'ru'; } };
    const fmtInt = (n) => { try { return Number(n).toLocaleString(intlLoc()); } catch (_) { return String(n); } };

    /** Laravel choice syntax: «{1} …|[2,4] …|[5,*] …». */
    function choice(str, n) {
        const parts = String(str || '').split('|');
        for (const p of parts) {
            const m = /^\s*(\{(\d+)\}|\[(\d+),(\d+|\*)\])\s*(.*)$/.exec(p);
            if (!m) continue;
            if (m[2] != null && +m[2] === n) return m[5];
            if (m[3] != null && n >= +m[3] && (m[4] === '*' || n <= +m[4])) return m[5];
        }
        return parts[parts.length - 1].replace(/^\s*(\{\d+\}|\[[^\]]+\])\s*/, '');
    }
    // Russian plural for «строка N» style counters lives in the strings themselves.

    /* ─────────── state ─────────── */
    const S = {
        obj: settings.load(SLUG, 'obj', 'sign'),
        src: settings.load(SLUG, 'src', 'text'),
        marks: settings.load(SLUG, 'marks', []) || [],
        manual: new Map(),
        showAll: false,
        photo: null,          // {canvas, lines:[{text, bbox, m}]}
        site: null,           // {pages:[page], current, host}
        lastEls: [], lastPairs: [], lastSummary: null,
        mockPair: null,
    };
    if (!L.OBJECTS[S.obj]) S.obj = 'sign';
    if (!['text', 'photo', 'site'].includes(S.src)) S.src = 'text';
    if (!Array.isArray(S.marks)) S.marks = [];

    /* ─────────── data ─────────── */
    let LEX = null, GLOSS = null, GLOSS_JSON = null, ANGL = null, anglPromise = null;
    const dataReady = Promise.all([
        fetch(C.lexicon, { cache: 'force-cache' }).then(r => r.ok ? r.json() : Promise.reject(new Error('lexicon_' + r.status))),
        fetch(C.glossary, { cache: 'force-cache' }).then(r => r.ok ? r.json() : Promise.reject(new Error('glossary_' + r.status))),
    ]).then(([lex, gl]) => {
        LEX = L.compileLexicon(lex);
        GLOSS_JSON = gl;
        GLOSS = L.compileGlossary(gl);
        return true;
    }).catch(e => { fail(e, 'slc_data', T.loading_dict); return false; });

    /** The anglicism dictionary — big (≈0.7 MB), so after the first answer, not before it. */
    function loadAngl() {
        if (anglPromise) return anglPromise;
        const E = window.AnglicismEngine;
        if (!E) return (anglPromise = Promise.resolve(null));
        anglPromise = fetch(C.anglManifest, { cache: 'no-cache' }).then(r => r.ok ? r.json() : { version: 'x' })
            .catch(() => ({ version: 'x' }))
            .then(m => fetch(C.anglBase + 'ru.json?v=' + encodeURIComponent(m.version || 'x'), { cache: 'force-cache' }))
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (!d) return null;
                const dict = E.compileDict(d);
                // Latin spellings the anglicism dictionary knows («feedback» → отзыв) widen our glossary.
                const extra = [];
                for (const e of (d.entries || [])) {
                    for (const v of (e.variants || [])) if (/^[a-z][a-z' -]*$/i.test(v)) extra.push([L.norm(v), e.alt || []]);
                }
                if (GLOSS_JSON && extra.length) GLOSS = L.compileGlossary(GLOSS_JSON, extra);
                ANGL = { dict, analyze: E.analyze, extra };
                return ANGL;
            })
            .catch(() => null);
        return anglPromise;
    }

    function ctx() {
        const brands = window.AnglicismEngine && window.AnglicismEngine.BUILTIN_BRANDS ? window.AnglicismEngine.BUILTIN_BRANDS : null;
        return { lex: LEX, gloss: GLOSS, marks: S.marks, angl: ANGL, extraBrands: brands };
    }

    /* ─────────── budgets (the /anglicism-checker ones) ─────────── */
    const LIM = C.limits || { links: {}, text: {}, pro_here: true, pro_url: '#', plan: '' };
    const isPro = () => !!(window.__tierLimits && window.__tierLimits.is_pro);
    const proHere = () => LIM.pro_here !== false;
    const pick = (m) => (isPro() || !proHere()) ? (+m.pro || 0) : (+m.free || 0);
    const TKEY = 'ac_text_chars';
    const today = () => new Date().toDateString();
    const textUsed = () => { try { const o = JSON.parse(localStorage.getItem(TKEY) || '{}'); return o.d === today() ? (o.n | 0) : 0; } catch (_) { return 0; } };
    const textWrite = (n) => { try { localStorage.setItem(TKEY, JSON.stringify({ d: today(), n: Math.max(0, n | 0) })); } catch (_) {} };
    let billedLen = 0;
    function billText(len) {
        const cap = pick(LIM.text || {});
        if (cap <= 0) return true;
        if (len > billedLen) { textWrite(textUsed() + (len - billedLen)); billedLen = len; }
        return true;
    }
    function textOver() { const cap = pick(LIM.text || {}); return cap > 0 && textUsed() > cap; }
    function renderTextLimit() {
        const el = $('slcTextLimit');
        const cap = pick(LIM.text || {});
        if (!el) return;
        const used = textUsed();
        if (isPro() || !proHere() || cap <= 0 || used < cap * 0.5) { el.hidden = true; return; }
        el.hidden = false;
        const paid = +((LIM.text || {}).pro) || 0;
        el.innerHTML = esc(tr(T.text_used, { used: fmtInt(Math.min(used, cap)), cap: fmtInt(cap) }))
            + (paid > cap ? ' · ' + esc(tr(T.limit_more, { plan: LIM.plan || '', paid: fmtInt(paid) })) + ' · <a href="' + esc(LIM.pro_url) + '">' + esc(T.limit_go) + '</a>' : '');
    }
    /* Pages of other sites: the server counts them, every answer carries `quota`. */
    function pagesCount(q) {
        try { if (q && window.__toolLimit && window.__toolLimit.serverCount) window.__toolLimit.serverCount(SLUG, q); } catch (_) {}
    }
    function pagesRefused(q) {
        try { if (window.__toolLimit && window.__toolLimit.serverRefused) window.__toolLimit.serverRefused(SLUG, q || null); } catch (_) {}
        try { if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'foreign_pages_quota', slug: SLUG, mode: S.src, cap: q && q.limit, pro: q && q.pro ? 1 : 0 }); } catch (_) {}
    }
    /* A delivered page answers the press: an earlier refusal's wall comes down. */
    function pagesDelivered() {
        try { if (window.__toolLimit && window.__toolLimit.clearNote) window.__toolLimit.clearNote(SLUG); } catch (_) {}
    }
    let pagesAsked = false;
    function pagesQuotaOnce() {
        if (pagesAsked) return;
        pagesAsked = true;
        ajax({ action: 'quota' }).catch(() => { pagesAsked = false; });
    }

    /* ─────────── object + source switches ─────────── */
    const LAWKEY = (o) => (L.OBJECTS[o] || L.OBJECTS.sign).law;
    function renderLaw() {
        const law = (T.law || {})[LAWKEY(S.obj)] || {};
        $('slcLawLine').textContent = law.applies || '';
        $('slcLawBody').innerHTML = '<div class="slc-law">'
            + ['applies', 'rule', 'except', 'words'].filter(k => law[k]).map(k => '<p>' + esc(law[k]) + '</p>').join('')
            + '</div>';
    }
    function setObj(o) {
        S.obj = o; settings.save(SLUG, 'obj', o);
        document.querySelectorAll('#slcObjs .slc-obj').forEach(b => {
            const on = b.dataset.obj === o;
            b.classList.toggle('on', on); b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        renderLaw();
        refresh();
    }
    function setSrc(s) {
        S.src = s; settings.save(SLUG, 'src', s);
        document.querySelectorAll('#slcSrc .tc-seg-b').forEach(b => {
            const on = b.dataset.src === s;
            b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        $('slcSrcText').hidden = s !== 'text';
        $('slcSrcPhoto').hidden = s !== 'photo';
        $('slcSrcSite').hidden = s !== 'site';
        if (s === 'site') pagesQuotaOnce();
        $('slcPhotoPanel').hidden = !(s === 'photo' && S.photo);
        $('slcSitePanel').hidden = !(s === 'site' && S.site && S.site.pages.length > 1);
        if (s === 'site' && S.obj === 'sign' && !S.site) setObj('site');
        else refresh();
    }

    /* ─────────── elements of the current carrier ─────────── */
    function currentItems() {
        if (S.src === 'text') {
            return ($('slcText').value || '').split(/\r?\n/).map(t => t.trim()).filter(Boolean).map(t => ({ kind: 'line', text: t }));
        }
        if (S.src === 'photo') {
            if (!S.photo) return [];
            return S.photo.lines.filter(l => l.text.trim()).map(l => ({ kind: 'line', text: l.text.trim(), m: l.m, ref: l }));
        }
        if (S.src === 'site') {
            const p = S.site && S.site.pages[S.site.current];
            return p ? p.items.map(it => ({ kind: it.kind, text: it.text, n: it.n, hidden: it.hidden })) : [];
        }
        return [];
    }

    function analyzeItems(items) {
        const c = ctx();
        const els = items.map((it) => Object.assign(L.analyzeElement(it.text, c), { item: it }));
        let page = null;
        if (S.src === 'site' && S.site) {
            const p = S.site.pages[S.site.current];
            page = L.pageLanguageVerdict({ lang: p.lang, alternates: p.alternates });
            if (page.code === 'page_foreign_version') {
                for (const el of els) {
                    for (const is of el.issues) if (is.code === 'latin') { is.code = 'latin_version'; is.sev = 'ok'; }
                    el.status = L.statusOf(el.issues);
                }
            }
        }
        const metrics = S.src === 'photo' ? items.map(it => it.m || null) : null;
        const pairs = L.pairDuplicates(els, { manual: S.manual, metrics });
        return { els, pairs, page };
    }

    /* ─────────── render ─────────── */
    const ICON = { fail: '⛔', warn: '⚠️', note: 'ℹ️', ok: '✅' };
    const pdfLink = (k, p) => (C.pdf && C.pdf[k] ? C.pdf[k] + '#page=' + p : '');
    const gramota = (w) => 'https://gramota.ru/poisk?query=' + encodeURIComponent(w) + '&mode=slovari';

    function refsHtml(entry, word) {
        const out = [];
        if (entry && entry.i) out.push('<a href="' + esc(pdfLink('i', entry.i)) + '" target="_blank" rel="noopener nofollow">' + esc(tr(V.ref_i, { p: entry.i })) + (entry.hom ? ' · ' + esc(tr(V.ref_hom, { n: entry.hom })) : '') + ' ↗</a>');
        if (entry && entry.o) out.push('<a href="' + esc(pdfLink('o', entry.o)) + '" target="_blank" rel="noopener nofollow">' + esc(tr(V.ref_o, { p: entry.o })) + ' ↗</a>');
        if (word) out.push('<a href="' + esc(gramota(String(word).toLowerCase())) + '" target="_blank" rel="noopener nofollow">' + esc(V.ref_gramota) + ' ↗</a>');
        return out.length ? '<span class="slc-refs">' + out.join('') + '</span>' : '';
    }

    function pairOptions(i, els, sel) {
        let h = '<option value="">' + esc(T.btn_pair) + '</option><option value="-1"' + (sel === -1 ? ' selected' : '') + '>' + esc(T.pair_none) + '</option>';
        els.forEach((e, j) => {
            if (j === i || !e.hasCyr) return;
            const label = tr(T.pair_line, { n: j + 1 }) + ' — ' + e.text.slice(0, 40);
            h += '<option value="' + j + '"' + (sel === j ? ' selected' : '') + '>' + esc(label) + '</option>';
        });
        return h;
    }

    function styleLines(st) {
        if (!st) return '';
        const out = [];
        if (st.size) {
            const p = Math.round(Math.abs(1 - (st.ratio || 1)) * 100);
            out.push(tr(T['size_' + st.size], { p }));
            if (st.approx) out.push(T.size_approx);
        }
        if (st.color) out.push(tr(T['color_' + st.color], { a: (st.cA || 0).toFixed(1), b: (st.cB || 0).toFixed(1) }));
        return out.length ? '<span class="slc-sub">' + esc(out.join(' ')) + '</span>' : '';
    }

    function issueHtml(is, i, el, els) {
        const sev = is.sev;
        const ic = '<i aria-hidden="true">' + ICON[sev] + '</i>';
        const e = is.entry;
        switch (is.code) {
            case 'latin': {
                const g = is.run.gloss || {};
                const phrase = (g.parts || []).some(p => p.phrase);
                const known = (g.parts || []).filter(p => !p.stop && p.ru && p.ru.length);
                const unknown = (g.parts || []).filter(p => !p.stop && !(p.ru && p.ru.length));
                let sug = '';
                if (g.suggest) {
                    sug = '<span class="slc-sub">' + esc(phrase || known.length < 2 ? V.latin_suggest : V.latin_word_by_word) + ' <span class="slc-sug">«' + esc(phrase || known.length < 2 ? g.suggest : known.map(p => p.en + ' — ' + p.ru.join(', ')).join('; ')) + '»</span></span>';
                }
                if (unknown.length) sug += '<span class="slc-sub">' + esc(V.latin_unknown) + ' (' + esc(unknown.map(p => p.en).join(', ')) + ')</span>';
                const canPair = S.src !== 'site';
                return '<li class="slc-is">' + ic + '<b>' + esc(V.latin) + ': «' + esc(is.run.text) + '»</b><span class="slc-sub">' + esc(V.latin_sub) + '</span>' + sug
                    + '<span class="slc-acts">'
                    + '<select class="tc-in" data-act="mark" data-run="' + esc(is.run.text) + '" aria-label="' + esc(T.btn_exempt) + '">'
                    + '<option value="">' + esc(T.btn_exempt) + '</option><option value="tm">' + esc(T.btn_is_tm) + '</option><option value="firm">' + esc(T.btn_is_firm) + '</option><option value="name">' + esc(T.btn_is_name) + '</option></select>'
                    + (canPair ? '<select class="tc-in" data-act="pair" data-i="' + i + '" aria-label="' + esc(T.btn_pair) + '">' + pairOptions(i, els, S.manual.has(i) ? S.manual.get(i) : null) + '</select>' : '')
                    + '<button type="button" class="tc-btn sm ghost" data-act="mock" data-src="' + esc(is.run.text) + '" data-dup="' + esc(g.suggest || '') + '" data-i="' + i + '">' + esc(T.btn_use_dup) + '</button>'
                    + '</span></li>';
            }
            case 'latin_dup': {
                const pr = is.pair;
                const where = pr.inline ? V.latin_dup_inline : tr(V.latin_dup, { where: tr(T.pair_line, { n: pr.dup + 1 }) + ' «' + els[pr.dup].text.slice(0, 60) + '»' });
                const head = pr.inline ? where : (is.partial ? V.latin_dup_partial + ' — ' + tr(T.pair_line, { n: pr.dup + 1 }) : where);
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.run.text) + '» → ' + esc(head) + '</b>'
                    + (pr.manual ? '<span class="slc-sub">' + esc(V.latin_dup_manual) + '</span>' : '')
                    + styleLines(pr.style)
                    + '<span class="slc-acts">'
                    + (S.src !== 'site' && !pr.inline ? '<select class="tc-in" data-act="pair" data-i="' + i + '" aria-label="' + esc(T.btn_pair) + '">' + pairOptions(i, els, pr.dup) + '</select>' : '')
                    + '<button type="button" class="tc-btn sm ghost" data-act="mock" data-src="' + esc(is.run.text) + '" data-dup="' + esc(pr.inline ? '' : els[pr.dup].text) + '" data-i="' + i + '" data-j="' + pr.dup + '">' + esc(T.btn_use_dup) + '</button>'
                    + '</span></li>';
            }
            case 'latin_version':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.run.text) + '»</b><span class="slc-sub">' + esc(V.page_foreign_version) + '</span></li>';
            case 'tm': case 'firm': {
                const m = is.run.mark || {};
                const no = String(m.no || '').replace(/\D/g, '');
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.run.text) + '» — ' + esc(V[is.code]) + (no ? ' № ' + esc(no) : '') + '</b>'
                    + (is.code === 'tm' && no ? '<span class="slc-refs"><a href="https://fips.ru/registers-doc-view/fips_servlet?DB=RUTM&DocNumber=' + esc(no) + '&TypeFile=html" target="_blank" rel="noopener nofollow">' + esc(T.mark_check) + ' ↗</a></span>' : '') + '</li>';
            }
            case 'name':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.run.text) + '» — ' + esc(V.name) + '</b><span class="slc-sub">' + esc(V.name_sub) + '</span></li>';
            case 'quote':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.run.text) + '» — ' + esc(V.quote) + '</b><span class="slc-sub">' + esc(V.quote_sub) + '</span>'
                    + '<span class="slc-acts"><button type="button" class="tc-btn sm ghost" data-act="name" data-i="' + i + '" data-run="' + esc(is.run.text) + '">' + esc(T.btn_is_name) + '</button></span></li>';
            case 'brand':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.run.text) + '» — ' + esc(V.brand) + '</b><span class="slc-sub">' + esc(V.brand_sub) + '</span>'
                    + '<span class="slc-acts"><button type="button" class="tc-btn sm ghost" data-act="tm" data-i="' + i + '" data-run="' + esc(is.run.text) + '">' + esc(T.btn_is_tm) + '</button></span></li>';
            case 'abbr':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.run.text) + '» — ' + esc(V.abbr) + '</b><span class="slc-sub">' + esc(is.run.abbrRu ? tr(V.abbr_ru, { ru: is.run.abbrRu.join(', ') }) : V.abbr_sub) + '</span></li>';
            case 'word_none': case 'word_sense': {
                const sub = is.code === 'word_sense' ? (e.sense || '') : V.word_none_sub;
                const sug = (e.sug || []).length ? '<span class="slc-sub">' + esc(tr(V.our_sug, { sug: '' })) + '<span class="slc-sug">' + esc(e.sug.join(', ')) + '</span></span>' : '';
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.word) + '» — ' + esc(V[is.code]) + '</b><span class="slc-sub">' + esc(sub) + '</span>' + sug + (e.note ? '<span class="slc-sub">' + esc(e.note) + '</span>' : '') + refsHtml(e, e.w) + '</li>';
            }
            case 'word_alt':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.word) + '» — ' + esc(V.word_alt) + '</b><span class="slc-sub">' + esc(tr(V.word_alt_sub, { alt: '«' + (e.alt || []).join('», «') + '»' })) + '</span>' + refsHtml(e, e.w) + '</li>';
            case 'word_orfo':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.word) + '» — ' + esc(V.word_orfo) + '</b><span class="slc-sub">' + esc(V.word_orfo_sub) + '</span>' + (e.note ? '<span class="slc-sub">' + esc(e.note) + '</span>' : '') + refsHtml(e, e.w) + '</li>';
            case 'word_razg':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.word) + '» — ' + esc(V.word_razg) + '</b><span class="slc-sub">' + esc(V.word_razg_sub) + '</span>' + refsHtml(e, e.w) + '</li>';
            case 'word_unknown':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.word) + '» — ' + esc(V.word_unknown) + '</b><span class="slc-sub">' + esc(V.word_unknown_sub) + '</span>'
                    + ((is.alts || []).length ? '<span class="slc-sub">' + esc(tr(V.our_sug, { sug: '' })) + '<span class="slc-sug">' + esc(is.alts.slice(0, 3).join(', ')) + '</span></span>' : '')
                    + '<span class="slc-refs"><a href="' + esc(gramota(is.lemma || is.word)) + '" target="_blank" rel="noopener nofollow">' + esc(V.ref_gramota) + ' ↗</a>'
                    + (C.pdf && C.pdf.i ? '<a href="' + esc(C.pdf.i) + '" target="_blank" rel="noopener nofollow">' + esc(tr(V.ref_i, { p: '…' })) + ' ↗</a>' : '') + '</span></li>';
            case 'word_parts':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.word) + '» — ' + esc(V.word_parts) + '</b><span class="slc-sub">' + esc(tr(V.word_parts_sub, { parts: is.parts.filter(p => p.st).map(p => p.p).join(', ') })) + '</span></li>';
            case 'spell':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.word) + '» — ' + esc(tr(V.spell, { spell: is.spell })) + '</b>'
                    + (is.state === 'ok' ? '<span class="slc-sub">' + esc(V.word_ok) + '. ' + esc(V.word_ok_sub) + '</span>' : is.state === 'orfo' ? '<span class="slc-sub">' + esc(V.word_orfo) + '. ' + esc(V.word_orfo_sub) + '</span>' : '')
                    + refsHtml(is.entry, is.spell) + '</li>';
            case 'mixed_script':
                return '<li class="slc-is">' + ic + '<b>«' + esc(is.word) + '» — ' + esc(V.mixed_script) + '</b><span class="slc-sub">' + esc(V.mixed_script_sub) + '</span></li>';
            default: return '';
        }
    }

    function highlight(el) {
        const marks = [];
        for (const is of el.issues) {
            if (is.start == null || is.code === 'address') continue;
            marks.push({ s: is.start, e: is.end, sev: is.sev });
        }
        marks.sort((a, b) => a.s - b.s || L.SEV[b.sev] - L.SEV[a.sev]);
        let out = '', pos = 0;
        for (const m of marks) {
            if (m.s < pos) continue;
            out += esc(el.text.slice(pos, m.s)) + '<span class="slc-w s-' + m.sev + '">' + esc(el.text.slice(m.s, m.e)) + '</span>';
            pos = m.e;
        }
        return out + esc(el.text.slice(pos));
    }

    function elHtml(el, i, els) {
        const it = el.item || {};
        const okWords = el.issues.filter(x => x.code === 'word_ok');
        const rest = el.issues.filter(x => x.code !== 'word_ok' && x.code !== 'address');
        let iss = rest.map(is => issueHtml(is, i, el, els)).join('');
        if (okWords.length) {
            iss += '<li class="slc-is"><i aria-hidden="true">✅</i><b>' + esc(V.word_ok) + ': ' + okWords.map(w => esc(w.word)).join(', ') + '</b><span class="slc-sub">' + esc(V.word_ok_sub) + '</span>'
                + '<span class="slc-refs">' + okWords.slice(0, 6).map(w => w.entry && w.entry.i ? '<a href="' + esc(pdfLink('i', w.entry.i)) + '" target="_blank" rel="noopener nofollow">' + esc(w.word) + ', ' + esc(tr(V.ref_i, { p: w.entry.i })) + ' ↗</a>' : '').join('') + '</span></li>';
        }
        const kindLabel = (T.kind || {})[it.kind] || '';
        return '<div class="slc-el s-' + el.status + '" data-i="' + i + '">'
            + '<div class="slc-el-h"><span class="slc-el-n">' + (i + 1) + '</span>'
            + (S.src === 'site' && kindLabel ? '<span class="slc-el-k">' + esc(kindLabel) + (it.n > 1 ? ' · ' + esc(tr(T.times, { n: it.n })) : '') + (it.hidden ? ' · ' + esc(T.hidden_item) : '') + '</span>' : '')
            + '<div class="slc-el-t">' + highlight(el) + '</div></div>'
            + (iss ? '<ul class="slc-iss">' + iss + '</ul>' : '')
            + '</div>';
    }

    let refreshTimer = 0;
    function refresh() {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(doRefresh, 60);
    }

    function doRefresh() {
        if (!LEX || !GLOSS) { dataReady.then(ok => { if (ok) doRefresh(); }); return; }
        const items = currentItems();
        if (S.src === 'text') {
            const len = ($('slcText').value || '').length;
            if (textOver() && len > billedLen) { setStatus(T.err_text_limit, true); renderTextLimit(); return; }
            billText(len);
            renderTextLimit();
        }
        const { els, pairs, page } = analyzeItems(items);
        S.lastEls = els; S.lastPairs = pairs; S.lastPage = page;
        const sum = L.summarize(els);
        if (page && page.sev === 'fail') { sum.status = 'fail'; sum.count.fail++; }
        S.lastSummary = sum;
        renderAnswer(els, pairs, sum, page);
        renderList(els, page);
        if (S.src === 'photo') drawBoxes(els);
        renderMaker();
        if (els.length && !ANGL) loadAngl().then(a => { if (a) refresh(); });
        if (els.length) {
            if (!S.reported) { S.reported = true; try { if (window.__stage) window.__stage('result'); } catch (_) {} }
            reportComplete(sum);
        }
    }

    // Результат объявляется ОДИН раз на ввод: прежний таймер «2,5 с после
    // перерисовки» срабатывал на каждой порции обхода сайта и на каждой паузе в
    // наборе (QA 23.10: три tool_complete на один обход). Ключ ввода — режим,
    // объект и отпечаток самого ввода; обход сайта объявляет итог по окончании.
    let completeT = 0, completeKey = null;
    function inputKey() {
        let v = '';
        if (S.src === 'text') v = ($('slcText').value || '').trim();
        else if (S.src === 'photo') v = S.photo && S.photo.key || '';
        else v = (S.site && S.site.pages[0] && S.site.pages[0].url || '') + '#' + (S.site ? S.site.pages.length : 0);
        let h = 0;
        for (let i = 0; i < v.length; i++) h = (h * 31 + v.charCodeAt(i)) | 0;
        return S.src + '|' + S.obj + '|' + v.length + ':' + h;
    }
    function reportComplete(sum, now) {
        if (S.src === 'site' && siteRunning && !now) return;
        clearTimeout(completeT);
        completeT = setTimeout(() => {
            const key = inputKey();
            if (key === completeKey) return;
            completeKey = key;
            try {
                if (window.__aeToolComplete) window.__aeToolComplete({ slug: SLUG, mode: S.src, obj: S.obj, status: sum.status, n: sum.elements, fail: sum.count.fail || 0, warn: sum.count.warn || 0 });
            } catch (_) {}
        }, now ? 0 : 2500);
    }

    function renderAnswer(els, pairs, sum, page) {
        const v = $('slcAnsV'), sub = $('slcAnsSub');
        if (!els.length) {
            v.textContent = '—'; v.className = 'tc-answer-v muted'; sub.textContent = T.ans_empty;
            $('slcStats').hidden = true; $('slcActions').hidden = true; $('slcListPanel').hidden = true;
            return;
        }
        const nFail = els.filter(e => e.status === 'fail').length + (page && page.sev === 'fail' ? 1 : 0);
        if (sum.status === 'fail') { v.textContent = tr(choice(T.ans_fail, nFail), { n: fmtInt(nFail) }); sub.textContent = T.ans_sub_fail; }
        else if (sum.status === 'warn') { v.textContent = T.ans_warn; sub.textContent = T.ans_sub_warn; }
        else { v.textContent = T.ans_ok; sub.textContent = T.ans_sub_ok; }
        v.className = 'tc-answer-v slc-ans-' + sum.status;
        v.style.color = sum.status === 'fail' ? 'var(--lg-danger)' : sum.status === 'warn' ? 'var(--lg-warn)' : 'var(--lg-success)';
        if (page && page.code !== 'page_ru') sub.textContent = V[page.code] + '. ' + sub.textContent;
        const latin = els.reduce((a, e) => a + e.issues.filter(i => i.code === 'latin').length, 0);
        const words = els.reduce((a, e) => a + e.issues.filter(i => i.code === 'word_none' || i.code === 'word_sense').length, 0);
        setStat('slcStEl', els.length, '');
        setStat('slcStLat', latin, latin ? 'bad' : 'good');
        setStat('slcStW', words, words ? 'bad' : 'good');
        setStat('slcStP', pairs.length, pairs.length ? 'good' : '');
        $('slcStats').hidden = false; $('slcActions').hidden = false;
    }
    function setStat(id, n, cls) {
        const el = $(id);
        el.className = 'tc-stat' + (cls ? ' ' + cls : '');
        el.querySelector('.tc-stat-v').textContent = fmtInt(n);
    }

    function renderList(els, page) {
        const panel = $('slcListPanel');
        if (!els.length) { panel.hidden = true; return; }
        panel.hidden = false;
        const showAll = S.showAll || els.length <= 12;
        $('slcFilter').hidden = els.length <= 12;
        $('slcFilter').textContent = S.showAll ? T.show_issues : T.show_all;
        $('slcFilter').setAttribute('aria-pressed', S.showAll ? 'true' : 'false');
        const p = S.src === 'site' && S.site ? S.site.pages[S.site.current] : null;
        $('slcListTitle').textContent = p ? (p.title || p.url) : '';
        $('slcListTitle').hidden = !p;
        let html = '';
        if (page && page.code !== 'page_ru') {
            html += '<div class="slc-el s-' + page.sev + '"><div class="slc-el-h"><div class="slc-el-t">' + esc(V[page.code]) + '</div></div>'
                + (page.ruAlt ? '<ul class="slc-iss"><li class="slc-is"><i aria-hidden="true">🔗</i><span class="slc-refs"><a href="' + esc(page.ruAlt.href) + '" target="_blank" rel="noopener nofollow">' + esc(page.ruAlt.href) + ' ↗</a></span></li></ul>' : '') + '</div>';
        }
        els.forEach((el, i) => {
            if (!showAll && (el.status === 'ok' || el.status === 'note') && !el.issues.some(x => x.code === 'latin_dup' || x.code === 'tm' || x.code === 'firm')) return;
            html += elHtml(el, i, els);
        });
        $('slcList').innerHTML = html;
    }

    function setStatus(msg, bad) {
        const el = $('slcStatus');
        if (!msg) { el.hidden = true; el.textContent = ''; return; }
        el.hidden = false; el.textContent = msg;
        el.style.color = bad ? 'var(--lg-danger)' : '';
    }

    /* ─────────── marks (trademarks / company names) ─────────── */
    function saveMarks() { settings.save(SLUG, 'marks', S.marks); }
    function renderMarks() {
        const ul = $('slcMarks');
        const cnt = $('slcMarksCount');
        if (cnt) cnt.textContent = S.marks.length ? String(S.marks.length) : '';
        if (!S.marks.length) { ul.innerHTML = '<li class="slc-empty">' + esc(T.marks_empty) + '</li>'; return; }
        ul.innerHTML = S.marks.map((m, k) => {
            const no = String(m.no || '').replace(/\D/g, '');
            return '<li><b>' + esc(m.text) + '</b><small>' + esc(m.kind === 'firm' ? T.mark_kind_firm : m.kind === 'name' ? T.mark_kind_name : T.mark_kind_tm) + (no ? ' № ' + esc(no) : '') + '</small>'
                + (m.kind === 'tm' && no ? '<a href="https://fips.ru/registers-doc-view/fips_servlet?DB=RUTM&DocNumber=' + esc(no) + '&TypeFile=html" target="_blank" rel="noopener nofollow">' + esc(T.mark_check) + ' ↗</a>' : '')
                + '<button type="button" class="slc-x" data-k="' + k + '" aria-label="' + esc(T.mark_remove) + '">✕</button></li>';
        }).join('');
    }
    function addMark(text, kind, no) {
        text = String(text || '').trim();
        if (!text) return;
        const key = L.norm(text);
        const had = S.marks.find(m => L.norm(m.text) === key);
        if (had) {
            if (no && kind === had.kind) { had.no = String(no).trim(); saveMarks(); renderMarks(); refresh(); }
            return;
        }
        S.marks.push({ text, kind: ['firm', 'name'].includes(kind) ? kind : 'tm', no: kind === 'name' ? '' : String(no || '').trim() });
        saveMarks(); renderMarks(); refresh();
        ae('feature_use', { f: 'mark_add', msg: kind });
    }

    /* ─────────── photo ─────────── */
    let ocrEng = null;
    function ocrOpen() {
        if (ocrEng) return Promise.resolve(ocrEng);
        if (!window.OcrEngine) return Promise.reject(Object.assign(new Error('OcrEngine missing'), { code: 'no_engine' }));
        const tp = C.tessPaths || {};
        return window.OcrEngine.open('rus+eng', {
            slug: SLUG,
            tesseractUrl: (C.tesseract || [])[0], tesseractFallbackUrl: (C.tesseract || [])[1],
            tesseract: { langPath: tp.langPath || undefined, workerPath: tp.workerPath || undefined, corePath: tp.corePath || undefined },
        }).then(e => (ocrEng = e));
    }
    function photoStatus(msg) { const el = $('slcPhotoStatus'); el.hidden = !msg; el.textContent = msg || ''; }
    function rotateQuarter(src, deg) {
        const c = document.createElement('canvas');
        if (deg === 180) { c.width = src.width; c.height = src.height; } else { c.width = src.height; c.height = src.width; }
        const g = c.getContext('2d');
        g.translate(c.width / 2, c.height / 2); g.rotate(deg * Math.PI / 180);
        g.drawImage(src, -src.width / 2, -src.height / 2);
        return c;
    }
    // Фото вывески: ориентация из Exif (снимок телефона «боком»), затем без опции
    // (старые браузеры её не знают), HEIC с iPhone — общим декодером сайта: зона
    // обещает HEIC, а Chrome и Firefox сами его не открывают (было «не удалось
    // прочитать фото»).
    async function bitmapOf(file) {
        try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch (_) { /* retry without the option */ }
        try { return await createImageBitmap(file); } catch (e) {
            if (window.HeicDecode && window.HeicDecode.isHeic(file)) {
                const jpg = await window.HeicDecode.toBlob(file, 'image/jpeg', 0.92);
                ae('feature_use', { f: 'photo_heic' });
                return await createImageBitmap(jpg);
            }
            throw e;
        }
    }
    async function loadPhoto(file) {
        let bmp;
        try { bmp = await bitmapOf(file); }
        catch (e) { fail(e, 'slc_photo_decode', T.err_photo); return; }
        const max = 2400, k = Math.min(1, max / Math.max(bmp.width, bmp.height));
        let cv = document.createElement('canvas');
        cv.width = Math.round(bmp.width * k); cv.height = Math.round(bmp.height * k);
        cv.getContext('2d').drawImage(bmp, 0, 0, cv.width, cv.height);
        try { bmp.close(); } catch (_) {}
        S.photo = { canvas: cv, lines: [], key: (file.size || 0) + ':' + (file.lastModified || 0) };
        S.manual = new Map();
        showPhoto();
        photoStatus(T.photo_engine);
        ae('feature_use', { f: 'photo_pick' });
        let data;
        try {
            const eng = await ocrOpen();
            photoStatus(T.photo_reading);
            data = await eng.recognize(cv, { orient: true });
        } catch (e) {
            photoStatus('');
            fail(e, 'slc_ocr', T.err_ocr);
            S.photo.lines = [{ text: '', bbox: null, m: null }];
            renderPhotoLines(); refresh();
            return;
        }
        if (data && data.rotation) { cv = rotateQuarter(cv, data.rotation); S.photo.canvas = cv; showPhoto(); }
        const g = cv.getContext('2d', { willReadFrequently: true });
        const img = g.getImageData(0, 0, cv.width, cv.height);
        const lines = mergeRows(((data && data.lines) || []).filter(l => l && String(l.text || '').trim() && l.bbox));
        S.photo.lines = lines.map(l => {
            const bb = l.bbox;
            const ms = bb ? L.measureBox(img.data, cv.width, bb) : null;
            const letters = String(l.text).replace(/[^A-Za-zА-Яа-яЁё]/g, '');
            const m = ms ? { h: ms.inkH || (bb.y1 - bb.y0), color: ms.color, bg: ms.bg, caps: !!letters && letters === letters.toUpperCase() } : null;
            return { text: String(l.text).trim(), bbox: bb, m };
        });
        photoStatus(S.photo.lines.length ? '' : T.photo_empty);
        ae('feature_use', { f: 'photo_ocr', msg: 'lines=' + S.photo.lines.length + ',engine=' + (data && data.engine) });
        renderPhotoLines();
        refresh();
    }
    /**
     * The recogniser boxes words or short runs; a sign line («COFFEE HOUSE») is
     * one finding. Boxes on one visual row, of similar height and with a gap no
     * wider than about one letter height, become one line; two plates hanging
     * side by side stay apart.
     */
    function mergeRows(lines) {
        const items = lines.map(l => ({ text: String(l.text).trim(), b: Object.assign({}, l.bbox) }));
        items.sort((a, b) => (a.b.y0 + a.b.y1) / 2 - (b.b.y0 + b.b.y1) / 2);
        const rows = [];
        for (const it of items) {
            const h = it.b.y1 - it.b.y0, cy = (it.b.y0 + it.b.y1) / 2;
            const row = rows.find(r => Math.abs(r.cy - cy) < 0.45 * Math.max(r.h, h) && Math.abs(r.h - h) / Math.max(r.h, h) < 0.4);
            if (row) { row.items.push(it); row.cy = (row.cy + cy) / 2; row.h = Math.max(row.h, h); }
            else rows.push({ cy, h, items: [it] });
        }
        const out = [];
        for (const r of rows) {
            r.items.sort((a, b) => a.b.x0 - b.b.x0);
            let cur = null;
            for (const it of r.items) {
                const h = it.b.y1 - it.b.y0;
                if (cur && it.b.x0 - cur.b.x1 < 1.3 * Math.max(h, cur.b.y1 - cur.b.y0)) {
                    cur.text += ' ' + it.text;
                    cur.b = { x0: Math.min(cur.b.x0, it.b.x0), y0: Math.min(cur.b.y0, it.b.y0), x1: Math.max(cur.b.x1, it.b.x1), y1: Math.max(cur.b.y1, it.b.y1) };
                } else {
                    if (cur) out.push(cur);
                    cur = { text: it.text, b: it.b };
                }
            }
            if (cur) out.push(cur);
        }
        return out.map(o => ({ text: o.text, bbox: o.b }));
    }

    function showPhoto() {
        const host = $('slcPhotoCanvas');
        const cv = S.photo.canvas;
        host.width = cv.width; host.height = cv.height;
        host.getContext('2d').drawImage(cv, 0, 0);
        $('slcBoxes').setAttribute('viewBox', '0 0 ' + cv.width + ' ' + cv.height);
        $('slcPhotoPanel').hidden = false;
    }
    const hex = (c) => c ? '#' + c.map(v => ('0' + Math.max(0, Math.min(255, v | 0)).toString(16)).slice(-2)).join('') : '';
    function renderPhotoLines() {
        const host = $('slcPhotoLines');
        host.innerHTML = S.photo.lines.map((l, k) => '<div class="slc-pline" data-k="' + k + '"><span class="slc-el-n">' + (k + 1) + '</span>'
            + '<input class="tc-in" value="' + esc(l.text) + '" placeholder="' + esc(T.photo_line_ph) + '" aria-label="' + esc(T.photo_line_ph) + ' ' + (k + 1) + '">'
            + (l.m ? '<small>' + esc(tr(T.photo_measured, { h: Math.round(l.m.h), c: hex(l.m.color) })) + '</small>' : '')
            + '<button type="button" class="slc-x" data-rm="' + k + '" aria-label="' + esc(T.photo_remove) + '">✕</button></div>').join('');
    }
    function drawBoxes(els) {
        const svg = $('slcBoxes');
        if (!S.photo) { svg.innerHTML = ''; return; }
        // elements map 1:1 onto non-empty photo lines, in order
        const withText = S.photo.lines.filter(l => l.text.trim());
        const fs = Math.max(16, Math.round(S.photo.canvas.width / 45));
        svg.style.setProperty('--slc-fs', fs + 'px');
        svg.innerHTML = withText.map((l, k) => {
            if (!l.bbox) return '';
            const st = els[k] ? els[k].status : 'ok';
            const b = l.bbox;
            return '<rect class="s-' + st + '" x="' + b.x0 + '" y="' + b.y0 + '" width="' + (b.x1 - b.x0) + '" height="' + (b.y1 - b.y0) + '" rx="4"></rect>'
                + '<text x="' + (b.x0 + 4) + '" y="' + Math.max(fs, b.y0 - 6) + '" font-size="' + fs + '">' + (k + 1) + '</text>';
        }).join('');
    }

    /* ─────────── site ─────────── */
    function normUrl(raw) {
        let s = String(raw || '').trim();
        if (!s) return null;
        if (/^[a-z][a-z0-9+.-]*:/i.test(s) && !/^https?:\/\//i.test(s)) return null;
        if (!/^https?:\/\//i.test(s)) s = 'https://' + s;
        try { const u = new URL(s); u.hash = ''; return u.href; } catch (_) { return null; }
    }
    // Токен — из <meta> в момент запроса (его обновляет __refreshCsrf), а не из
    // снимка страницы: страница приходит из кэша с чужим токеном, и КАЖДЫЙ запрос
    // обхода сайта ловил 419 и шёл вторым кругом (QA 23.10: по 2 × 419 на страницу).
    function csrfNow() {
        const m = document.querySelector('meta[name="csrf-token"]');
        return (m && m.content) || C.csrf;
    }
    async function ajax(body) {
        if (window.__csrfReady) { try { await window.__csrfReady(); } catch (_) { /* the fetch wrapper retries */ } }
        const r = await fetch(C.ajax, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfNow(), 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
        });
        let j = {};
        try { j = await r.json(); } catch (_) {}
        if (j && j.quota) pagesCount(j.quota);
        if (!r.ok) { const e = new Error(j.error || ('http_' + r.status)); e.code = j.error || ('http_' + r.status); e.status = r.status; e.body = j; throw e; }
        return j;
    }
    function errText(e) {
        const c = e && e.code;
        if (c === 'invalid_url') return T.err_url;
        if (c === 'blocked_target') return T.err_blocked;
        if (c === 'robots_disallowed') return T.err_robots;
        if (c === 'rate_limited') return T.err_rate;
        if (c === 'scan_busy') return tr(T.site_busy, { s: String((e.body && e.body.retry_after) || 60) });
        if (c === 'no_sitemap') return T.err_no_sitemap;
        return tr(T.err_fetch, { why: String(c || (e && e.message) || '') });
    }
    function pageFromExtract(ex, url, source) {
        return { url: url || ex.url || '', title: ex.title || url || '', lang: ex.lang || '', alternates: ex.alternates || [], items: ex.items || [], source };
    }
    function showPages(pages, current) {
        S.site = { pages, current: current || 0 };
        S.manual = new Map();
        renderSiteTable();
        $('slcSitePanel').hidden = pages.length < 2;
        refresh();
    }
    function renderSiteTable() {
        const tb = $('slcSiteTable').querySelector('tbody');
        if (!S.site) { tb.innerHTML = ''; return; }
        const c = ctx();
        tb.innerHTML = S.site.pages.map((p, k) => {
            if (!p.sum) {
                const els = p.items.map(it => L.analyzeElement(it.text, c));
                L.pairDuplicates(els, {});
                const pv = L.pageLanguageVerdict({ lang: p.lang, alternates: p.alternates });
                if (pv.code === 'page_foreign_version') els.forEach(el => { el.issues.forEach(is => { if (is.code === 'latin') is.sev = 'ok'; }); el.status = L.statusOf(el.issues); });
                p.sum = L.summarize(els);
                if (pv.sev === 'fail') { p.sum.status = 'fail'; p.sum.count.fail++; }
            }
            const f = p.sum.count.fail || 0, w = p.sum.count.warn || 0;
            return '<tr class="slc-site-row' + (k === S.site.current ? ' now' : '') + '" data-k="' + k + '" tabindex="0" title="' + esc(T.site_page_open) + '">'
                + '<td>' + esc(p.title || p.url) + '<br><small>' + esc(p.url) + '</small></td>'
                + '<td class="num"><span class="slc-badge s-' + (f ? 'fail' : 'ok') + '">' + f + '</span></td>'
                + '<td class="num"><span class="slc-badge s-' + (w ? 'warn' : 'ok') + '">' + w + '</span></td></tr>';
        }).join('');
    }

    async function checkPage() {
        const url = normUrl($('slcUrl').value);
        if (!url) { toast(T.err_url, 'warning'); return; }
        settings.save(SLUG, 'url', $('slcUrl').value.trim());
        setStatus(T.site_loading);
        $('slcPageBtn').disabled = true;
        try {
            const d = await ajax({ action: 'page', url });
            const ex = X.fromHtml(d.html, d.final_url || url);
            showPages([pageFromExtract(ex, d.final_url || url, 'server')], 0);
            pagesDelivered();
            setStatus('');
            ae('feature_use', { f: 'page_check', msg: 'items=' + ex.items.length + (d.cached ? ',cached' : '') + (d.repeat ? ',repeat' : '') });
        } catch (e) {
            if (e.code === 'page_quota') { setStatus(''); pagesRefused(e.body && e.body.quota); }
            else setStatus(errText(e), true);
            ae('tool_error', { msg: 'page:' + (e.code || e.message) });
        } finally { $('slcPageBtn').disabled = false; }
    }

    async function linksOf(url, max, scanId) {
        const d = await ajax({ action: 'page', url, scan: 1, scan_id: scanId });
        const base = d.final_url || url;
        const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch (_) { return ''; } };
        const out = [base];
        const seen = new Set([base.replace(/#.*$/, '')]);
        const doc = new DOMParser().parseFromString(String(d.html || ''), 'text/html');
        for (const a of doc.querySelectorAll('a[href]')) {
            let u;
            try { u = new URL(a.getAttribute('href'), base); } catch (_) { continue; }
            if (!/^https?:$/.test(u.protocol) || host(u.href) !== host(base)) continue;
            u.hash = '';
            // Метки рекламы — та же страница. С ними её и robots.txt закрывает
            // («Disallow: /*utm_»), и обход тратил места на отказы (coffeemania.ru:
            // три из пяти страниц дня — «/menu?utm_source=…», QA 23.10).
            for (const k of Array.from(u.searchParams.keys())) {
                if (/^(utm_|yclid$|gclid$|fbclid$|_openstat$|ysclid$|roistat)/i.test(k)) u.searchParams.delete(k);
            }
            if (/\.(png|jpe?g|gif|webp|svg|ico|css|js|json|xml|pdf|zip|mp[34]|webm|docx?|xlsx?)(\?|$)/i.test(u.pathname)) continue;
            if (seen.has(u.href)) continue;
            seen.add(u.href); out.push(u.href);
            if (out.length >= max) break;
        }
        return out;
    }

    let siteStop = false, siteRunning = false;
    async function checkSite() {
        if (siteRunning) return;
        const url = normUrl($('slcUrl').value);
        if (!url) { toast(T.err_url, 'warning'); return; }
        siteRunning = true; siteStop = false;
        $('slcSiteBtn').disabled = true;
        let scanId = null, quotaStop = false;
        const want = parseInt($('slcPages').value, 10) || 30;
        const pages = [];
        try {
            setStatus(T.site_loading);
            // The server opens the check (one at a time per person) and says how
            // many pages it may read: Премиум 200, otherwise what the day has left.
            const st = await ajax({ action: 'scan-start', pages: want });
            scanId = st.scan_id;
            if (st.quota && st.quota.remaining <= 0) { setStatus(''); pagesRefused(st.quota); return; }
            const maxPages = Math.min(want, st.allowed || want);
            let urls = [];
            try {
                const disco = await ajax({ action: 'sitemap', url });
                urls = (disco.urls || []).slice(0, maxPages);
            } catch (e) {
                if (e.code !== 'no_sitemap') throw e;
            }
            // No sitemap (small business sites often have none): the start page's
            // own same-site links are the list. Reading it again in the scan below
            // is a repeat of the same address today — it does not count twice.
            if (!urls.length) urls = await linksOf(url, maxPages, scanId);
            if (!urls.length) throw Object.assign(new Error('no_sitemap'), { code: 'no_sitemap' });
            setStatus('');
            $('slcSiteProg').hidden = false;
            const total = urls.length;
            let cursor = 0, done = 0;
            const prog = () => {
                const pct = Math.round(done / total * 100);
                $('slcSiteFill').style.width = pct + '%';
                $('slcSiteProg').querySelector('.slc-bar').setAttribute('aria-valuenow', String(pct));
                $('slcSiteProgT').textContent = tr(T.site_progress, { done: fmtInt(done), total: fmtInt(total) });
            };
            prog();
            const worker = async () => {
                while (!siteStop && !quotaStop && cursor < urls.length) {
                    const u = urls[cursor++];
                    try {
                        const d = await ajax({ action: 'page', url: u, scan: 1, scan_id: scanId });
                        const ex = X.fromHtml(d.html, d.final_url || u);
                        pages.push(pageFromExtract(ex, d.final_url || u, 'scan'));
                    } catch (e) {
                        if (e.code === 'page_quota') {
                            // The day is spent: the page was not read, so it is not a row.
                            if (!quotaStop) { quotaStop = true; pagesRefused(e.body && e.body.quota); }
                            break;
                        }
                        pages.push({ url: u, title: u + ' — ' + errText(e), lang: '', alternates: [], items: [], failed: e.code || 'fail' });
                    }
                    done++; prog();
                    if (pages.length === 1 || done % 5 === 0) showPages(pages.slice(), S.site ? S.site.current : 0);
                }
            };
            await Promise.all([worker(), worker()]);
            showPages(pages, 0);
            if (quotaStop) setStatus(tr(T.site_quota_stopped, { n: fmtInt(pages.length) }));
            ae('feature_use', { f: 'site_scan_done', msg: 'pages=' + pages.length + (siteStop ? ',stopped' : '') + (quotaStop ? ',quota' : '') });
            if (S.lastSummary && S.lastSummary.elements) reportComplete(S.lastSummary, true);
        } catch (e) {
            setStatus(errText(e), true);
            ae('tool_error', { msg: 'site:' + (e.code || e.message) });
        } finally {
            if (scanId) { try { await ajax({ action: 'scan-end', scan_id: scanId }); } catch (_) {} }
            siteRunning = false;
            $('slcSiteBtn').disabled = false;
            $('slcSiteProg').hidden = true;
        }
    }

    /* ─────────── mock-up of the equal duplicate ─────────── */
    function mockSvg() {
        const src = $('slcMockSrc').value.trim() || 'SALE';
        const dup = $('slcMockDup').value.trim() || 'РАСПРОДАЖА';
        const font = $('slcMockFont').value, weight = $('slcMockWeight').value;
        const color = $('slcMockColor').value, bg = $('slcMockBg').value;
        const order = $('slcMockOrder').value;
        const W = 1200;
        const cv = document.createElement('canvas').getContext('2d');
        const measure = (txt, size) => { cv.font = weight + ' ' + size + 'px ' + font; return cv.measureText(txt).width; };
        const lines = order === 'src' ? [src, dup] : [dup, src];
        let size = 140;
        if (order === 'side') {
            const gap = 80;
            while (size > 12 && measure(dup, size) + measure(src, size) + gap > W - 120) size -= 2;
            const H = Math.round(size * 1.9);
            const wd = measure(dup, size);
            return { w: W, h: H, svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H + '">'
                + '<rect width="100%" height="100%" fill="' + esc(bg) + '"/>'
                + '<g font-family="' + esc(font) + '" font-weight="' + esc(weight) + '" font-size="' + size + '" fill="' + esc(color) + '">'
                + '<text x="60" y="' + Math.round(H / 2 + size * 0.35) + '">' + esc(dup) + '</text>'
                + '<text x="' + Math.round(60 + wd + gap) + '" y="' + Math.round(H / 2 + size * 0.35) + '">' + esc(src) + '</text></g></svg>' };
        }
        while (size > 12 && Math.max(measure(lines[0], size), measure(lines[1], size)) > W - 120) size -= 2;
        const lh = Math.round(size * 1.35);
        const H = Math.round(lh * 2 + size * 0.7);
        const y1 = Math.round(size * 0.35 + lh * 0.85), y2 = y1 + lh;
        return { w: W, h: H, svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H + '">'
            + '<rect width="100%" height="100%" fill="' + esc(bg) + '"/>'
            + '<g font-family="' + esc(font) + '" font-weight="' + esc(weight) + '" font-size="' + size + '" fill="' + esc(color) + '" text-anchor="middle">'
            + '<text x="' + (W / 2) + '" y="' + y1 + '">' + esc(lines[0]) + '</text>'
            + '<text x="' + (W / 2) + '" y="' + y2 + '">' + esc(lines[1]) + '</text></g></svg>' };
    }
    function renderMock() { $('slcMock').innerHTML = mockSvg().svg; renderMaker(); }
    function useInMock(src, dup, i, j) {
        $('slcMockSrc').value = src || '';
        $('slcMockDup').value = (dup || '').toUpperCase() === dup ? dup : (src === src.toUpperCase() ? String(dup || '').toUpperCase() : dup || '');
        const m = S.src === 'photo' && S.photo ? currentItems()[i] && currentItems()[i].m : null;
        if (m && m.color) { $('slcMockColor').value = hex(m.color); if (m.bg) $('slcMockBg').value = hex(m.bg); }
        S.mockPair = { i, j };
        $('slcMockDrawer').open = true;
        renderMock();
        try { $('slcMockDrawer').scrollIntoView({ block: 'nearest' }); } catch (_) {}
        ae('feature_use', { f: 'mock_open' });
    }
    async function downloadMock(kind) {
        const m = mockSvg();
        const base = 'dubl-vyveski';
        if (kind === 'svg') {
            await window.__downloadFile(new Blob([m.svg], { type: 'image/svg+xml' }), base + '.svg');
            return;
        }
        const img = new Image();
        const url = URL.createObjectURL(new Blob([m.svg], { type: 'image/svg+xml' }));
        try {
            await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
            const c = document.createElement('canvas'); c.width = m.w; c.height = m.h;
            c.getContext('2d').drawImage(img, 0, 0);
            const blob = await window.__canvasBlob(c, 'image/png');
            await window.__downloadFile(blob, base + '.png');
        } catch (e) { fail(e, 'slc_mock_png'); }
        finally { URL.revokeObjectURL(url); }
    }

    /* ─────────── text for the sign maker ─────────── */
    function makerText() {
        const M = T.maker_text || {};
        const src = $('slcMockSrc').value.trim();
        const dup = $('slcMockDup').value.trim();
        // Изготовителю — то, что нужно СДЕЛАТЬ: надписи без русского дубля (с нашим
        // вариантом перевода) и дубли, которые меньше или бледнее; равнозначная пара
        // «SALE / Распродажа» в задание не идёт. Раньше при любой найденной паре
        // задание описывало только пары — а «VAPE ZONE» без дубля из него выпадал.
        const pairs = [];
        if (src || dup) pairs.push([src, dup]);
        else {
            for (const el of S.lastEls) for (const is of el.issues) {
                if (is.code === 'latin') {
                    // Пословный перевод («гламур улица») — черновик, а не готовая надпись.
                    const g = is.run.gloss || {};
                    const phrase = (g.parts || []).some(p => p.phrase);
                    const known = (g.parts || []).filter(p => !p.stop && p.ru && p.ru.length);
                    pairs.push([is.run.text, g.suggest || '…', !(phrase || known.length < 2) || !g.suggest]);
                }
                else if (is.code === 'latin_dup' && !is.pair.inline && is.sev !== 'ok') pairs.push([is.run.text, S.lastEls[is.pair.dup].text]);
            }
            if (!pairs.length) for (const p of S.lastPairs) if (!p.inline) pairs.push([p.run.text, S.lastEls[p.dup].text]);
        }
        if (!pairs.length) return '';
        const mm = parseFloat(String($('slcMakerMm').value || '').replace(',', '.'));
        const where = $('slcWhere').value.trim();
        const font = $('slcMockFont').selectedOptions[0] ? $('slcMockFont').selectedOptions[0].textContent : '';
        const lines = [M.head || ''];
        if (where) lines.push(tr(M.where, { where }));
        for (const [s, d, draft] of pairs.slice(0, 12)) {
            lines.push('');
            lines.push(tr(M.src, { src: s }));
            lines.push(tr(draft && M.dup_draft ? M.dup_draft : M.dup, { dup: d }));
        }
        lines.push('');
        lines.push(M.rule || '');
        lines.push(tr(M.font, { font: src ? ' (в макете — ' + font + ')' : '' }));
        lines.push(tr(M.color, { color: $('slcMockColor').value }));
        lines.push(tr(M.size, { mm: mm > 0 ? ' — ' + fmtInt(Math.round(mm)) + ' мм' : '' }));
        lines.push(M.place || '');
        lines.push('');
        let date = '';
        try { date = new Date().toLocaleDateString(intlLoc()); } catch (_) { date = window.__localYmd(); }
        lines.push(tr(M.footer, { date }));
        return lines.join('\n');
    }
    function renderMaker() { const t = $('slcMakerText'); if (t) t.value = makerText(); }

    /* ─────────── copy summary ─────────── */
    function summaryText() {
        const out = [$('slcAnsV').textContent + '. ' + $('slcAnsSub').textContent, ''];
        S.lastEls.forEach((el, i) => {
            if (el.status === 'ok' && !el.issues.some(x => x.code === 'latin_dup')) return;
            const notes = el.issues.filter(x => x.sev !== 'ok' || x.code === 'latin_dup').map(x => issuePlain(x)).filter(Boolean);
            out.push((i + 1) + '. ' + el.text + (notes.length ? ' — ' + notes.join('; ') : ''));
        });
        return out.join('\n');
    }
    function issuePlain(is) {
        switch (is.code) {
            case 'latin': return V.latin + (is.run.gloss && is.run.gloss.suggest ? ' (' + V.latin_suggest + ' «' + is.run.gloss.suggest + '»)' : '');
            case 'latin_dup': {
                const st = is.pair.style;
                let extra = '';
                if (st && st.size) extra += ' ' + tr(T['size_' + st.size], { p: Math.round(Math.abs(1 - (st.ratio || 1)) * 100) });
                if (st && st.color) extra += ' ' + tr(T['color_' + st.color], { a: (st.cA || 0).toFixed(1), b: (st.cB || 0).toFixed(1) });
                return '«' + is.run.text + '» → ' + (is.pair.inline ? V.latin_dup_inline : tr(V.latin_dup, { where: tr(T.pair_line, { n: is.pair.dup + 1 }) })) + (is.partial ? ' (' + V.latin_dup_partial + ')' : '') + extra;
            }
            case 'word_none': case 'word_sense': return '«' + is.word + '» — ' + V[is.code] + (is.entry.sug ? ' (' + is.entry.sug.join(', ') + ')' : '');
            case 'word_alt': return '«' + is.word + '» — ' + V.word_alt + ' (' + (is.entry.alt || []).join(', ') + ')';
            case 'word_orfo': return '«' + is.word + '» — ' + V.word_orfo;
            case 'word_unknown': return '«' + is.word + '» — ' + V.word_unknown;
            case 'spell': return '«' + is.word + '» — ' + tr(V.spell, { spell: is.spell });
            case 'brand': return '«' + is.run.text + '» — ' + V.brand;
            case 'quote': return '«' + is.run.text + '» — ' + V.quote;
            case 'name': return '«' + is.run.text + '» — ' + V.name;
            case 'abbr': return '«' + is.run.text + '» — ' + V.abbr;
            case 'word_razg': return '«' + is.word + '» — ' + V.word_razg;
            case 'mixed_script': return '«' + is.word + '» — ' + V.mixed_script;
            default: return '';
        }
    }

    /* ─────────── report (PDF) ─────────── */
    function refPlain(e) {
        const r = [];
        if (e && e.i) r.push(tr(V.ref_i, { p: e.i }) + (e.hom ? ' (' + tr(V.ref_hom, { n: e.hom }) + ')' : ''));
        if (e && e.o) r.push(tr(V.ref_o, { p: e.o }));
        return r.join('; ');
    }
    function buildReport() {
        const R = T.rep || {};
        const law = (T.law || {})[LAWKEY(S.obj)] || {};
        const obj = (T.obj || {})[S.obj] || {};
        let when = '';
        try { when = new Date().toLocaleString(intlLoc(), { dateStyle: 'long', timeStyle: 'short' }); } catch (_) { when = new Date().toISOString(); }
        const where = $('slcWhere').value.trim() || (S.src === 'site' && S.site ? S.site.pages[S.site.current].url : '');
        const owner = $('slcOwner').value.trim();
        // Every line with a finding gets a row; lines with nothing to say are listed
        // compactly below — the report still shows WHAT was checked, without six
        // pages of dashes for a whole web page.
        const clean = [];
        const rows = S.lastEls.map((el, i) => {
            if (el.issues.every(x => x.code === 'address')) { clean.push((i + 1) + '. ' + el.text); return ''; }
            const notes = el.issues.filter(x => x.code !== 'address').map(x => {
                const p = issuePlain(x) || (x.code === 'word_ok' ? '«' + x.word + '» — ' + V.word_ok : x.code === 'tm' || x.code === 'firm' ? '«' + x.run.text + '» — ' + V[x.code] : x.code === 'latin_version' ? V.page_foreign_version : '');
                return p;
            }).filter(Boolean);
            const refs = el.issues.map(x => x.entry ? refPlain(x.entry) : x.code === 'latin' || x.code === 'latin_dup' ? 'ч. 2 ст. 3 53-ФЗ' : x.code === 'tm' || x.code === 'firm' ? 'п. 4 ст. 10.1 ЗоЗПП' + (x.run.mark && x.run.mark.no ? ', № ' + x.run.mark.no : '') : '').filter(Boolean);
            const st = el.status;
            return '<tr><td>' + (i + 1) + '</td><td>' + esc(el.text) + '</td><td style="color:' + (st === 'fail' ? '#b42318' : st === 'warn' ? '#a15c00' : '#1a7f37') + '">' + (notes.length ? esc(notes.join('; ')) : '—') + '</td><td>' + esc(Array.from(new Set(refs)).join('; ')) + '</td></tr>';
        }).join('');
        const marks = S.marks.length ? '<h3>' + esc(R.marks) + '</h3><ul>' + S.marks.map(m => '<li>' + esc(m.text) + ' — ' + esc(m.kind === 'firm' ? T.mark_kind_firm : m.kind === 'name' ? T.mark_kind_name : T.mark_kind_tm) + (m.no ? ' № ' + esc(m.no) + (m.kind === 'tm' ? ' (https://fips.ru/registers-doc-view/fips_servlet?DB=RUTM&DocNumber=' + esc(String(m.no).replace(/\D/g, '')) + '&TypeFile=html)' : '') : '') + '</li>').join('') + '</ul>' : '';
        const pairs = S.lastPairs.length ? '<h3>' + esc(R.pairs) + '</h3><ul>' + S.lastPairs.map(p => {
            const st = p.style;
            let extra = '';
            if (st) {
                if (st.size) extra += ' ' + tr(T['size_' + st.size], { p: Math.round(Math.abs(1 - (st.ratio || 1)) * 100) });
                if (st.color) extra += ' ' + tr(T['color_' + st.color], { a: (st.cA || 0).toFixed(1), b: (st.cB || 0).toFixed(1) });
            }
            return '<li>«' + esc(p.run.text) + '» → ' + esc(p.inline ? V.latin_dup_inline : '«' + S.lastEls[p.dup].text + '»') + esc(extra) + '</li>';
        }).join('') + '</ul>' : '';
        let photo = '';
        if (S.src === 'photo' && S.photo) {
            try {
                const c = document.createElement('canvas');
                const k = Math.min(1, 1000 / S.photo.canvas.width);
                c.width = Math.round(S.photo.canvas.width * k); c.height = Math.round(S.photo.canvas.height * k);
                const g = c.getContext('2d'); g.drawImage(S.photo.canvas, 0, 0, c.width, c.height);
                g.lineWidth = 3; g.font = 'bold 22px sans-serif';
                S.photo.lines.filter(l => l.text.trim()).forEach((l, n) => {
                    if (!l.bbox) return;
                    const st = S.lastEls[n] ? S.lastEls[n].status : 'ok';
                    g.strokeStyle = st === 'fail' ? '#e5484d' : st === 'warn' ? '#f5a524' : '#2fa36b';
                    g.strokeRect(l.bbox.x0 * k, l.bbox.y0 * k, (l.bbox.x1 - l.bbox.x0) * k, (l.bbox.y1 - l.bbox.y0) * k);
                    g.fillStyle = g.strokeStyle; g.fillText(String(n + 1), l.bbox.x0 * k + 3, Math.max(22, l.bbox.y0 * k - 5));
                });
                photo = '<h3>' + esc(R.photo) + '</h3><img alt="" src="' + c.toDataURL('image/jpeg', 0.85) + '" style="max-width:100%;border:1px solid #ccc">';
            } catch (_) {}
        }
        let checked = (LEX && LEX.meta && LEX.meta.checked) || '';
        try { if (checked) checked = new Date(checked + 'T12:00:00').toLocaleDateString(intlLoc(), { day: 'numeric', month: 'long', year: 'numeric' }); } catch (_) {}
        const method = (T.method || []).map(m => '<p>' + esc(tr(m, { n: fmtInt(LEX ? LEX.words : 0), date: checked })) + '</p>').join('');
        const fam = window.HtmlPdf && window.HtmlPdf.FAMILY ? window.HtmlPdf.FAMILY.sans : 'Arial, sans-serif';
        return '<div class="slc-rep" style="font-family:' + esc(fam) + ';color:#111;background:#fff;font-size:11pt;line-height:1.45">'
            + '<style>.slc-rep h2{font-size:15pt;margin:0 0 8pt}.slc-rep h3{font-size:12pt;margin:14pt 0 4pt}.slc-rep table{border-collapse:collapse;width:100%;font-size:9.5pt}.slc-rep td,.slc-rep th{border:1px solid #bbb;padding:4pt 5pt;vertical-align:top;text-align:left}.slc-rep th{background:#f1f1f1}.slc-rep p{margin:4pt 0}.slc-rep .meta td{border:0;padding:1pt 6pt 1pt 0}.slc-rep tr{break-inside:avoid}</style>'
            + '<h2>' + esc(R.title) + '</h2>'
            + '<table class="meta"><tr><td><b>' + esc(R.when) + '</b></td><td>' + esc(when) + '</td></tr>'
            + '<tr><td><b>' + esc(R.object) + '</b></td><td>' + esc(obj.t || '') + ' (' + esc(obj.s || '') + ')</td></tr>'
            + (where ? '<tr><td><b>' + esc(R.where) + '</b></td><td>' + esc(where) + '</td></tr>' : '')
            + (owner ? '<tr><td><b>' + esc(R.owner) + '</b></td><td>' + esc(owner) + '</td></tr>' : '') + '</table>'
            + '<h3>' + esc(R.summary) + '</h3><p><b>' + esc($('slcAnsV').textContent) + '.</b> ' + esc($('slcAnsSub').textContent) + '</p>'
            + '<h3>' + esc(R.laws) + '</h3>' + ['applies', 'rule', 'except', 'words'].filter(k => law[k]).map(k => '<p>' + esc(law[k]) + '</p>').join('')
            + '<h3>' + esc(R.rows) + '</h3><table><thead><tr><th>' + esc(R.col_n) + '</th><th>' + esc(R.col_text) + '</th><th>' + esc(R.col_verdict) + '</th><th>' + esc(R.col_ref) + '</th></tr></thead><tbody>' + rows + '</tbody></table>'
            + (clean.length ? '<p style="margin-top:8pt"><b>' + esc(tr(R.clean, { n: fmtInt(clean.length) })) + '</b></p><p style="font-size:9pt;color:#444">' + esc(clean.slice(0, 400).join(' · ')) + (clean.length > 400 ? ' …' : '') + '</p>' : '')
            + marks + pairs + photo
            + '<h3>' + esc(R.method) + '</h3>' + method
            + '<p style="margin-top:18pt">' + esc(R.sign) + '</p>'
            + '<p style="color:#555;font-size:9pt">' + esc(R.footer) + '</p></div>';
    }
    let pdfBusy = false;
    async function downloadReport() {
        if (pdfBusy || !S.lastEls.length) return;
        pdfBusy = true;
        const btn = $('slcPdf'); btn.disabled = true;
        setStatus(T.pdf_busy);
        const host = $('slcRepHost');
        const name = ((T.rep && T.rep.file) || 'report') + '-' + window.__localYmd() + '.pdf';
        try {
            if (window.HtmlPdf && typeof window.HtmlPdf.fromElement === 'function') {
                try { window.HtmlPdf.installFonts(); } catch (_) {}
                host.innerHTML = buildReport();
                if (document.fonts && document.fonts.ready) { try { await document.fonts.ready; } catch (_) {} }
                const blob = await window.HtmlPdf.fromElement(host.firstElementChild, {
                    pages: 'auto', page: 'a4', marginsMm: { t: 16, r: 14, b: 16, l: 16 },
                    footer: '{page} / {pages}', title: (T.rep && T.rep.title) || '', lang: 'ru', as: 'blob',
                });
                const ok = await window.__downloadFile(blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' }), name);
                if (ok !== false) ae('feature_use', { f: 'report_pdf', msg: 'vector' });
            } else if (window.__generatePDF) {
                host.innerHTML = buildReport();
                await window.__generatePDF({ element: host.firstElementChild, filename: name });
                ae('feature_use', { f: 'report_pdf', msg: 'raster' });
            }
            setStatus('');
        } catch (e) {
            setStatus('');
            fail(e, 'slc_report_pdf');
        } finally {
            host.innerHTML = '';
            pdfBusy = false; btn.disabled = false;
        }
    }

    /* ─────────── wiring ─────────── */
    function wire() {
        document.querySelectorAll('#slcObjs .slc-obj').forEach(b => b.addEventListener('click', () => { if (S.obj !== b.dataset.obj) ae('feature_use', { f: 'obj_pick', msg: b.dataset.obj }); setObj(b.dataset.obj); }));
        $('slcObjs').addEventListener('keydown', (e) => {
            if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
            const list = Array.from(document.querySelectorAll('#slcObjs .slc-obj'));
            const k = list.findIndex(b => b.dataset.obj === S.obj);
            const n = (k + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length;
            e.preventDefault(); setObj(list[n].dataset.obj); list[n].focus();
        });
        document.querySelectorAll('#slcSrc .tc-seg-b').forEach(b => b.addEventListener('click', () => { if (S.src !== b.dataset.src) ae('feature_use', { f: 'src_pick', msg: b.dataset.src }); setSrc(b.dataset.src); }));
        const ta = $('slcText');
        ta.value = settings.load(SLUG, 'text', '');
        ta.addEventListener('input', () => { settings.save(SLUG, 'text', ta.value); S.manual = new Map(); refresh(); });
        $('slcSample').addEventListener('click', () => { ta.value = C.sample || ''; billedLen = 0; settings.save(SLUG, 'text', ta.value); S.manual = new Map(); refresh(); ae('feature_use', { f: 'sample' }); });
        $('slcClear').addEventListener('click', () => { ta.value = ''; billedLen = 0; settings.save(SLUG, 'text', ''); S.manual = new Map(); refresh(); ta.focus(); });

        const zone = $('slcUpload');
        if (zone && window.__bindUploaderFile) window.__bindUploaderFile(zone, (f) => { loadPhoto(f); });
        if (zone) zone.addEventListener('tool-uploader:clear', () => { S.photo = null; $('slcPhotoPanel').hidden = true; refresh(); });
        $('slcPhotoLines').addEventListener('input', (e) => {
            const row = e.target.closest('.slc-pline'); if (!row || !S.photo) return;
            S.photo.lines[+row.dataset.k].text = e.target.value; refresh();
        });
        $('slcPhotoLines').addEventListener('click', (e) => {
            const b = e.target.closest('[data-rm]'); if (!b || !S.photo) return;
            S.photo.lines.splice(+b.dataset.rm, 1); S.manual = new Map(); renderPhotoLines(); refresh();
        });
        $('slcAddLine').addEventListener('click', () => {
            if (!S.photo) return;
            S.photo.lines.push({ text: '', bbox: null, m: null }); renderPhotoLines();
            const inputs = $('slcPhotoLines').querySelectorAll('input'); if (inputs.length) inputs[inputs.length - 1].focus();
        });

        $('slcUrl').value = settings.load(SLUG, 'url', '');
        $('slcUrl').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); checkPage(); } });
        $('slcPageBtn').addEventListener('click', checkPage);
        $('slcSiteBtn').addEventListener('click', checkSite);
        $('slcSiteStop').addEventListener('click', () => { siteStop = true; });
        $('slcSiteTable').addEventListener('click', (e) => {
            const tr0 = e.target.closest('.slc-site-row'); if (!tr0 || !S.site) return;
            S.site.current = +tr0.dataset.k; S.manual = new Map(); renderSiteTable(); refresh();
        });
        $('slcSiteTable').addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            const tr0 = e.target.closest('.slc-site-row'); if (!tr0 || !S.site) return;
            S.site.current = +tr0.dataset.k; renderSiteTable(); refresh();
        });
        $('slcHtmlBtn').addEventListener('click', () => {
            const html = $('slcHtml').value;
            if (!html.trim()) return;
            const ex = X.fromHtml(html, '');
            showPages([pageFromExtract(ex, '', 'paste')], 0);
            ae('feature_use', { f: 'paste_html', msg: 'items=' + ex.items.length });
        });
        const bm = $('slcBm');
        try { bm.setAttribute('href', X.bookmarklet(C.toolUrl)); } catch (_) {}
        bm.addEventListener('click', (e) => { e.preventDefault(); toast(T.bm_click, 'info'); });

        $('slcMarkNo').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); $('slcMarkAdd').click(); } });
        $('slcMarkAdd').addEventListener('click', () => {
            addMark($('slcMarkText').value, $('slcMarkKind').value, $('slcMarkNo').value);
            $('slcMarkText').value = ''; $('slcMarkNo').value = '';
        });
        $('slcMarkText').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); $('slcMarkAdd').click(); } });
        $('slcMarks').addEventListener('click', (e) => {
            const b = e.target.closest('.slc-x[data-k]'); if (!b) return;
            S.marks.splice(+b.dataset.k, 1); saveMarks(); renderMarks(); refresh();
        });

        $('slcList').addEventListener('click', (e) => {
            const b = e.target.closest('button[data-act]'); if (!b) return;
            const act = b.dataset.act;
            if (act === 'tm' || act === 'firm' || act === 'name') markFromList(b.dataset.run, act);
            else if (act === 'mock') {
                useInMock(b.dataset.src, b.dataset.dup, +b.dataset.i, b.dataset.j != null ? +b.dataset.j : null);
            }
        });
        function markFromList(run, act) {
            addMark(run, act, '');
            if (act !== 'tm') return;
            // the number goes into the report and the FIPS link — ask for it right away
            $('slcMarksDrawer').open = true;
            $('slcMarkText').value = run;
            $('slcMarkKind').value = 'tm';
            try { $('slcMarksDrawer').scrollIntoView({ block: 'nearest' }); } catch (_) {}
            try { $('slcMarkNo').focus({ preventScroll: true }); } catch (_) {}
        }
        $('slcList').addEventListener('change', (e) => {
            const mk = e.target.closest('select[data-act="mark"]');
            if (mk) { if (mk.value) markFromList(mk.dataset.run, mk.value); return; }
            const s = e.target.closest('select[data-act="pair"]'); if (!s) return;
            const i = +s.dataset.i;
            if (s.value === '') S.manual.delete(i); else S.manual.set(i, +s.value);
            ae('feature_use', { f: 'pair_manual' });
            refresh();
        });
        $('slcFilter').addEventListener('click', () => { S.showAll = !S.showAll; refresh(); });
        ['slcMockSrc', 'slcMockDup', 'slcMockFont', 'slcMockWeight', 'slcMockColor', 'slcMockBg', 'slcMockOrder'].forEach(id => $(id).addEventListener('input', renderMock));
        $('slcMockSvg').addEventListener('click', () => downloadMock('svg'));
        $('slcMockPng').addEventListener('click', () => downloadMock('png'));
        $('slcMockDrawer').addEventListener('toggle', () => { if ($('slcMockDrawer').open && !$('slcMock').innerHTML) renderMock(); });
        $('slcMakerMm').addEventListener('input', renderMaker);
        $('slcWhere').addEventListener('input', renderMaker);
        $('slcMakerBtn').addEventListener('click', () => { renderMaker(); $('slcMakerDrawer').open = true; try { $('slcMakerDrawer').scrollIntoView({ block: 'nearest' }); } catch (_) {} });
        const copy = async (txt) => {
            try { await navigator.clipboard.writeText(txt); toast(T.copied, 'success'); }
            catch (_) { toast(T.copied, 'info'); }
        };
        $('slcMakerCopy').addEventListener('click', () => { copy($('slcMakerText').value); ae('feature_use', { f: 'maker_copy' }); });
        $('slcCopy').addEventListener('click', () => copy(summaryText()));
        $('slcPdf').addEventListener('click', downloadReport);
        $('slcWhere').value = settings.load(SLUG, 'where', '');
        $('slcOwner').value = settings.load(SLUG, 'owner', '');
        $('slcWhere').addEventListener('change', () => settings.save(SLUG, 'where', $('slcWhere').value));
        $('slcOwner').addEventListener('change', () => settings.save(SLUG, 'owner', $('slcOwner').value));
        document.addEventListener('tier-bootstrap:ready', () => { renderTextLimit(); });
    }

    async function fromBookmarklet() {
        if (!/^#slcz?=/.test(location.hash)) return false;
        const ex = await X.readFragment(location.hash);
        try { history.replaceState(null, '', location.pathname + location.search); } catch (_) {}
        if (!ex) return false;
        setSrc('site');
        if (S.obj !== 'site') setObj('site');
        if (/^https?:\/\//i.test(ex.url || '')) $('slcUrl').value = ex.url;
        showPages([pageFromExtract(ex, ex.url, 'bookmarklet')], 0);
        toast(tr(T.bm_received, { n: fmtInt(ex.items.length) }), 'success');
        ae('feature_use', { f: 'bookmarklet_in', msg: 'items=' + ex.items.length });
        return true;
    }

    function boot() {
        wire();
        renderMarks();
        renderLaw();
        setObj(S.obj);
        setSrc(S.src);
        fromBookmarklet();
        // the bookmarklet may land on a tab that already shows this page
        window.addEventListener('hashchange', () => { fromBookmarklet(); });
        renderTextLimit();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
